import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { HttpErrorResponse } from '@angular/common/http';
import { throwError, of } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { BaseDAO } from './BaseDAO';
import { AppSetting } from '../AppSetting';
import { RailgunErrorFactory, RailgunError } from '../RailgunError';
import { AppSingletonUtil } from '../Util/AppSingletonUtil';
import { HttpStatusCodeEnum } from '../Enum/HttpstatusCodeEnum';
import { BaseService } from '../Service/BaseService';
import { HtmlStringConst } from '../Enum/HtmlStringConst';

/**
 * BaseWebApiDAO
 * WebAPI用のベースクラス
 */
export class BaseWebApiDAO extends BaseDAO {

	// WebAPIで使用するトークン
	public token: string;

	// 最後に実行されたリクエストのURL
	private latestUrl: string;

	// リクエストオプションのデフォルト
	// @see https://developer.mozilla.org/ja/docs/Web/HTTP/Headers
	private httpOptions = {
		headers: new HttpHeaders({
		  'Content-Type':  'application/json',
		  // Authorization: this.token, // tokenはボディに入れる
		})
	  };

	/**
	 * コンストラクタ
	 * 呼び出し元のサービスを貰って、エラー伝搬先とする
	 */
	constructor(svc: BaseService) {
		super(svc);
		this.token = AppSingletonUtil.getToken(); // HACK: リクエストボディにいれるので、トークンをプロパティにいれている
	}

	/**
	 * getHttpClient
	 * HTTPClientの取得
	 */
	private getHttpClient(): HttpClient {
		return AppSingletonUtil.getHttpClient();
	}

	/**
	 * postRequest
	 * postリクエストを送信してresponseを取得。
	 * パースしてモデルのArrayで返す。
	 * エラー時は、結果がnullになって、this.errorList にエラー内容が入る。
	 *
	 * @param url APIのURL 送信先のURL。基本はAppSettingApiに定義してある物で指定。
	 * @param request リクエストデータ
	 * @param httpOptions ヘッダー等をカスタムする場合に指定する。
	 */
	public async postRequest(url: string, request: any, httpOptions=this.httpOptions, parseFull=false ): Promise<any|null> {

		// 実行するリクエストを持っておく
		this.latestUrl = url;

		// リクエストを送信
		const data = await this.getHttpClient()
			// ポストリクエストで送信
			.post<any>(url, request, httpOptions)
			.pipe(
				// 結果受取の前処理
				// リトライ回数を指定
				retry(AppSetting.HTTP_REQUEST_RETRY),
				// エラーのキャッチ処理 postリクエストのエラー
				catchError( (err: HttpErrorResponse) => throwError(err) )

			).toPromise() // subscribeしないで、Promiseにして、await使って、この場で実行する。
			.then(
				(ret) => {
					// レスポンスとれたら、パースして中身取り出す。
					if( httpOptions !== this.httpOptions && parseFull){
						// 専用処理をわたされたら、それでパース。エラー処理入らないので注意。
						this.log('----parseFull---');
						return this.responseParseDispatch(ret);
					} else {
						// 汎用のパース処理でBodyを取り出す
						const body = this.commonParseResult(ret); // 中でエラーになった時はthis.errorHandler()が呼ばれる
						return body;
					}
			}).catch(
				(ret) => {
					// 途中でエラーが発生した場合は、エラーハンドラー呼び出す。
					this.errorHandler(ret);
					return null;
				}
			);

		return data;
	}


	/**
	 * errorHandler
	 * リクエストエラーハンドラー
	 * @param errData エラーオブジェクト
	 */
	private errorHandler(errData: any){

		// エラーメッセージ先頭
		const msgHead = 'url:' + this.latestUrl + '\n BaseWebApiDAO.errorHandle: ';
		// エラーオブジェクト生成
		const railgunErr = RailgunErrorFactory.createRailgunErrorObj(errData, msgHead);

		this.log(railgunErr);

		// 伝搬用にセット
		this.setErrorObj(railgunErr);

	}


	/**
	 * responseParseDispatch
	 * content-type判定版のレスポンスパース。
	 * @param resp HTTPのレスポンス。ヘッダーとかも入ってる。
	 */
	public async responseParseDispatch(resp: HttpResponse<any>) {

		this.log('----------------------responseParseDispatch-------------------------');
		this.log(resp);

		// レスポンスヘッダーの分離
		const headers: { [name: string]: string } = {};
		resp.headers.keys().forEach( key => {
			headers[key] = resp.headers.get(key);
		});

		this.log(headers);

		// content-type の取得
		const contentType = headers['content-type'] ? headers['content-type'] : 'application/zip';
		// content-length の取得
		const contentLength = headers['content-length'] ? headers['content-length'] : 0;


		// HACK: レスポンス判定ディスパッチは、整理が必要
		this.log('------ content-type output.-------');
		this.log('header content-type:' + contentType);
		this.log('header content-length:' + contentLength);
		this.log('http status:' + resp.status);
		this.log('http url:' + resp.url);

		// text-csvはダウンロード
		if ( contentType.match(/text.csv/) ) {
			return this.resultDownlod(resp.body, contentType, 'download.csv');
		}

		// json だったら汎用パース
		if( contentType.match(/json/)) {
			return this.commonParseResult(resp.body);
		}

		// zip だったらダウンロード
		if( contentType.match(/zip/)) {
			return this.resultDownlod(resp.body, contentType, 'download.zip');
		}

		// 引っかからなかったやつはエラーにする
		this.log('------ content-type unmatch.-------');
		this.log('header content-type:' + contentType);
		this.log('header content-length:' + contentLength);
		this.log('http status:' + resp.status);
		this.log('http url:' + resp.url);
		this.log('body:');
		this.log(resp.body);

		return false;

	}


	/**
	 * commonParseResult
	 * 汎用のパース処理
	 * @param data レスポンスのBody
	 */
	public async commonParseResult(data: any) {

		this.log('------------------commonResult-----------------------');
		this.log(data);

		// レスポンスのプロパティチェック
		if ( !data || !data.body || !data.code ) {
			// Bodyなしエラー
			const obj = new RailgunError();
			obj.rawMessage = '-----------BaseWebAPI post error　body/code = null ---------------';
			obj.latestUrl = this.latestUrl;
			this.errorHandler(obj);
			return null;
		}

		// Bodyの中のcodeチェック
		switch (Number(data.code)) {
			case HttpStatusCodeEnum.OK:
				// 成功
				return data.body;

			case HttpStatusCodeEnum.FORBIDDEN:
				// 認証エラー
				// エラーobjに設定
				const authError = new RailgunError();
				authError.setHttpStatusFlag(Number(data.code));
				authError.unauthorized = true; // 認証エラーをセット
				authError.respBody	= data.body;
				authError.respMessage = data.body;
				authError.rawMessage = '---authError----';
				authError.latestUrl = this.latestUrl;
				this.errorHandler(authError);
				return null;

			case HttpStatusCodeEnum.NOT_FOUND:
				// データなし
				const notfoundError = new RailgunError();
				notfoundError.setHttpStatusFlag(Number(data.code));
				notfoundError.dataNotFound = true; // データなしエラーをセット
				notfoundError.respBody	= data.body;
				notfoundError.respMessage = data.body;
				notfoundError.rawMessage = '---notfoundError----';
				notfoundError.latestUrl = this.latestUrl;
				this.errorHandler(notfoundError);
				return null;

			case HttpStatusCodeEnum.NOT_ACCEPTABLE:
				// サーバ側のエラーメッセージを伝搬する
				// data.body からメッセージを取り出し
				let bodyMsg = '';
				if( Array.isArray(data.body)) {
					data.body.forEach( (element,i) => {
						bodyMsg += element + HtmlStringConst.BR; // 全部に改行を付ける
					});
				}
				// エラーobjに設定
				const dataError = new RailgunError();
				dataError.setHttpStatusFlag(Number(data.code));
				dataError.saverValidationError = true; // サーバ側のバリデーションエラーをセット
				dataError.respBody	= bodyMsg; // エラーメッセージはrespBodyに入れる
				dataError.respMessage = bodyMsg;
				dataError.rawMessage = '---dataError body.code----';
				dataError.latestUrl = this.latestUrl;
				this.errorHandler(dataError);
				return null;

			default:
				// 汎用エラー。５００とか
				// エラーobjに設定
				const otherError = new RailgunError();
				otherError.setHttpStatusFlag(Number(data.code));
				otherError.retryPossibility = false; // リトライ不許可
				otherError.respBody = data.body;
				otherError.respMessage = data.body;
				otherError.rawMessage = '---otherError body.code----';
				otherError.latestUrl = this.latestUrl;
				// エラーobjをセット
				this.errorHandler(otherError);
				return null;

		}


	}


	/**
	 * resultDownlod
	 * リンクでのダウンロード処理
	 * @param body レスポンスのBody
	 * @param contentType コンテンツのタイプ
	 * @param fineName ダウンロードのファイル名
	 */
	public async resultDownlod(body: any, contentType: string, fineName: string){

		// ダウンロードファイルの受取処理
		// 受け取ったバイナリをBlobにして、ブラウザにリンク経由でダウンロードさせる
		const blob = new Blob([body], { type: contentType});
		const url = window.URL.createObjectURL(blob);

		// リンクを作って、ダウンロードファイルを渡す
		const link = document.createElement('a') as HTMLAnchorElement;
		document.body.appendChild(link);
		link.href = url;
		link.download = fineName;
		await link.click(); // ここでＤＬになる

		// リンクを消して後始末
		window.URL.revokeObjectURL(url);
		document.body.removeChild(link);

		return true;

	}

}
